import axios from 'axios';
import { getImageUrl } from './api';

const API_URL = process.env.REACT_APP_API_URL || 'https://nivaas.app';

// Process visitor records to ensure image URLs are properly formatted
// and visitor statuses are consistent with their check-in/check-out state
const processVisitorImageUrls = (visitors) => {
  if (!Array.isArray(visitors)) return [];

  console.log('Processing visitor data, count:', visitors.length);
  let statusFixCount = 0;

  const processed = visitors.map(visitor => {
    const processedVisitor = { ...visitor };

    // Process photo URL
    if (processedVisitor.photo_url) {
      processedVisitor.photo_url = getImageUrl(processedVisitor.photo_url);
    }

    // Process ID document URL
    if (processedVisitor.id_document_url) {
      processedVisitor.id_document_url = getImageUrl(processedVisitor.id_document_url);
    }

    // VALIDATION STEP 1: Fix inconsistent statuses - if a visitor has checked in but not out,
    // they should be marked as active regardless of the status from the server
    if (processedVisitor.time_in && !processedVisitor.time_out && processedVisitor.status !== 'active') {
      console.log(`API fix: Correcting status for visitor ${processedVisitor.id} (${processedVisitor.visitor_name}) from ${processedVisitor.status} to active`);
      processedVisitor.status = 'active';
      statusFixCount++;
    }

    // VALIDATION STEP 2: Conversely, if a visitor has both time_in and time_out, they should be marked as completed
    if (processedVisitor.time_in && processedVisitor.time_out && processedVisitor.status !== 'completed') {
      console.log(`API fix: Correcting status for visitor ${processedVisitor.id} from ${processedVisitor.status} to completed`);
      processedVisitor.status = 'completed';
      statusFixCount++;
    }

    return processedVisitor;
  });

  if (statusFixCount > 0) {
    console.log(`API processing fixed ${statusFixCount} visitor status inconsistencies`);
  }

  return processed;
};

// Get a specific visitor by ID
export const getVisitorById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/api/visitors/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    // Process the visitor data if it exists
    if (response.data) {
      // Apply the same processing as multiple visitors
      const processedVisitors = processVisitorImageUrls([response.data]);
      return { data: processedVisitors[0] };
    }

    return response;
  } catch (error) {
    console.error(`Error fetching visitor ${id}:`, error);
    return { error: error.message };
  }
};

// Get all visitors (using search endpoint for reliability)
export const getAllVisitors = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/visitors/search`, {
      params: {
        limit: 100,
        status: 'all'
      },
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    // Process the visitor data if it exists
    if (response.data && response.data.visitors) {
      const processedVisitors = processVisitorImageUrls(response.data.visitors);
      return {
        data: processedVisitors
      };
    }

    return { data: [] };
  } catch (error) {
    console.error('Error fetching all visitors:', error);
    return { error: error.message };
  }
};

// Get active visitors directly using search endpoint
export const getActiveVisitors = async (organizationId) => {
  try {
    const response = await axios.get(`${API_URL}/api/visitors/search`, {
      params: {
        status: 'active',
        has_time_in: true,
        has_time_out: false,
        include_organization: true,
        organization_id: organizationId,
        limit: 500
      },
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    return processVisitorImageUrls(response.data);
  } catch (error) {
    console.error('Error fetching active visitors:', error);
    return [];
  }
};

// Search for visitors with given parameters
export const searchVisitors = async (params) => {
  try {
    // Convert villa filter to unit_id if present
    if (params.villa) {
      params.unit_id = params.villa;
      delete params.villa;
    }

    console.log('searchVisitors called with params:', params);
    const response = await axios.get(`${API_URL}/api/visitors/search`, {
      params,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    console.log('searchVisitors response:', response.data);

    // Process the visitor data if it exists
    if (response.data) {
      // Handle both array and object response formats
      const visitors = Array.isArray(response.data) ? response.data :
        response.data.visitors ? response.data.visitors : [];

      const processedVisitors = processVisitorImageUrls(visitors);

      // Return in consistent format with proper pagination data
      return {
        data: {
          visitors: processedVisitors,
          total: response.data.total || processedVisitors.length,
          page: response.data.page || 1,
          limit: response.data.limit || processedVisitors.length,
          totalPages: response.data.totalPages || Math.ceil((response.data.total || processedVisitors.length) / (response.data.limit || processedVisitors.length))
        }
      };
    }

    return response;
  } catch (error) {
    console.error('Error searching visitors:', error);
    return { error: error.message };
  }
};

// Create a new visitor
export const createVisitor = async (visitorData) => {
  try {
    const response = await axios.post(`${API_URL}/api/visitors`, visitorData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Error creating visitor:', error);
    return { error: error.message };
  }
};

// Update visitor check-in data
export const updateVisitorCheckin = async (id, checkinData) => {
  try {
    const response = await axios.put(`${API_URL}/api/visitors/${id}/checkin`, checkinData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    // Make sure status is consistent with check-in
    if (response.data && response.data.status !== 'active' && checkinData.status === 'active') {
      console.log(`Fixing inconsistent check-in status response for visitor ${id}`);
      response.data.status = 'active';
    }

    return response;
  } catch (error) {
    console.error(`Error updating check-in for visitor ${id}:`, error);
    return { error: error.message };
  }
};

// Check out a visitor
export const checkoutVisitor = async (id, checkoutData) => {
  try {
    const response = await axios.put(`${API_URL}/api/visitors/${id}/checkout`, checkoutData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    // Make sure status is consistent with check-out
    if (response.data && response.data.status !== 'completed' && checkoutData.status === 'completed') {
      console.log(`Fixing inconsistent check-out status response for visitor ${id}`);
      response.data.status = 'completed';
    }

    return response;
  } catch (error) {
    console.error(`Error checking out visitor ${id}:`, error);
    return { error: error.message };
  }
};

export default {
  getVisitorById,
  searchVisitors,
  createVisitor,
  updateVisitorCheckin,
  checkoutVisitor,
  getAllVisitors,
  getActiveVisitors
}; 